import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Camera, Save, Shield, QrCode, Upload, Trash2, Edit, ArrowLeft, Bell, CheckCircle, XCircle, AlertTriangle, Globe, Calendar, MessageCircle } from 'lucide-react';
import ProfileGigsSection from '@/features/artist-mode/components/ProfileGigsSection';
import SupportThreadView from '@/features/artist-mode/components/SupportThreadView';
import { getMobileStyles, ysr2Mobile } from '../styles/mobileDesignSystem';
import MobileCard from './MobileCard';
import { 
  initializePushNotifications, 
  isSubscribedToPushNotifications,
  probePushSubscriptionSupport,
  unsubscribeFromPushNotifications,
} from '@/lib/pushSubscription';
import { sendTestNotificationDirect } from '@/lib/pushNotifications';
import { useToast } from '@/components/ui/use-toast';
import { COUNTRIES } from '@/lib/countries';

const VALID_PROFILE_TABS = new Set(['profile', 'gigs', 'support', 'security', 'notifications']);

const MobileProfile = ({ 
  authUserId,
  profile,
  formData,
  onInputChange,
  onSave,
  onImageUpload,
  onRemoveImage,
  uploadingImage,
  loading,
  onUpdateEmail,
  onUpdatePassword,
  onDeleteAccount,
  country = 'GB',
  currency = 'GBP',
  onCountryChange,
  /** Initial bottom-nav tab when opening Profile (e.g. `notifications` when linked with `?tab=alerts`). */
  defaultTab = 'profile',
}) => {
  const [activeTab, setActiveTab] = useState(() =>
    defaultTab && VALID_PROFILE_TABS.has(defaultTab) ? defaultTab : 'profile',
  );
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [securityData, setSecurityData] = useState({
    newEmail: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    deleteConfirmation: ''
  });
  const [updatingEmail, setUpdatingEmail] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  
  // Push notification state
  const [pushNotificationStatus, setPushNotificationStatus] = useState({
    isSupported: false,
    setupHint: 'unsupported',
    permission: 'default',
    isSubscribed: false,
    isLoading: false
  });
  
  const { toast } = useToast();

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'gigs', label: 'Gigs', icon: Calendar },
    { id: 'support', label: 'Support', icon: MessageCircle },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'notifications', label: 'Alerts', icon: Bell },
  ];

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      onImageUpload(e);
      setShowImagePicker(false);
    }
  };

  const handleSecurityInputChange = (e) => {
    const { name, value } = e.target;
    setSecurityData(prev => ({ ...prev, [name]: value }));
  };

  const handleUpdateEmail = async () => {
    if (!securityData.newEmail || !securityData.currentPassword) {
      return;
    }
    setUpdatingEmail(true);
    try {
      await onUpdateEmail(securityData.newEmail, securityData.currentPassword);
      setSecurityData(prev => ({ ...prev, newEmail: '', currentPassword: '' }));
      setShowEmailModal(false);
    } catch (error) {
      console.error('Email update error:', error);
    } finally {
      setUpdatingEmail(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!securityData.currentPassword || !securityData.newPassword || !securityData.confirmPassword) {
      return;
    }
    if (securityData.newPassword !== securityData.confirmPassword) {
      return;
    }
    setUpdatingPassword(true);
    try {
      await onUpdatePassword(securityData.currentPassword, securityData.newPassword, securityData.confirmPassword);
      setSecurityData(prev => ({ ...prev, currentPassword: '', newPassword: '', confirmPassword: '' }));
      setShowPasswordModal(false);
    } catch (error) {
      console.error('Password update error:', error);
    } finally {
      setUpdatingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (securityData.deleteConfirmation !== 'delete') {
      return;
    }
    setDeletingAccount(true);
    try {
      await onDeleteAccount();
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Delete account error:', error);
    } finally {
      setDeletingAccount(false);
    }
  };

  // Push notification functions
  const checkPushNotificationStatus = async () => {
    try {
      setPushNotificationStatus(prev => ({ ...prev, isLoading: true }));
      const probe = await probePushSubscriptionSupport();
      const permission = typeof Notification !== 'undefined' ? Notification.permission : 'default';
      let isSubscribed = false;

      if (probe.available) {
        isSubscribed = await isSubscribedToPushNotifications();
      }

      setPushNotificationStatus({
        isSupported: probe.available,
        setupHint: probe.hint,
        permission,
        isSubscribed,
        isLoading: false
      });
    } catch (error) {
      console.error('Error checking push notification status:', error);
      setPushNotificationStatus(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleEnablePushNotifications = async () => {
    if (!authUserId) {
      toast({
        variant: 'destructive',
        title: 'Sign in required',
        description: 'Log in again, then enable push notifications.',
      });
      return;
    }
    try {
      setPushNotificationStatus(prev => ({ ...prev, isLoading: true }));
      
      await initializePushNotifications(authUserId);
      
      // Refresh status
      await checkPushNotificationStatus();
      
      toast({
        title: "Push Notifications Enabled",
        description: "You'll now receive notifications for song requests and tips",
      });
    } catch (error) {
      console.error('Error enabling push notifications:', error);
      toast({
        variant: "destructive",
        title: "Failed to Enable Notifications",
        description: error.message,
      });
      setPushNotificationStatus(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleTestNotification = async () => {
    if (!authUserId) {
      toast({
        variant: 'destructive',
        title: 'Sign in required',
        description: 'Log in again to send a test notification.',
      });
      return;
    }
    try {
      setPushNotificationStatus(prev => ({ ...prev, isLoading: true }));
      
      const ok = await sendTestNotificationDirect(authUserId);
      if (!ok) {
        throw new Error('Could not send — enable push first or check your connection.');
      }
      
      toast({
        title: "Test Notification Sent",
        description: "Check your notifications to verify it's working",
      });
    } catch (error) {
      console.error('Error sending test notification:', error);
      toast({
        variant: "destructive",
        title: "Test Failed",
        description: error.message || 'Could not send the test notification.',
      });
    } finally {
      setPushNotificationStatus(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleDisablePushNotifications = async () => {
    if (!authUserId) {
      toast({
        variant: 'destructive',
        title: 'Sign in required',
        description: 'Log in again, then disable push notifications.',
      });
      return;
    }
    try {
      setPushNotificationStatus(prev => ({ ...prev, isLoading: true }));
      const ok = await unsubscribeFromPushNotifications(authUserId);
      if (!ok) {
        throw new Error('Could not clear this device subscription.');
      }
      await checkPushNotificationStatus();
      toast({
        title: 'Push Notifications Disabled',
        description: 'Tap Enable when you want alerts again on this device.',
      });
    } catch (error) {
      console.error('Error disabling push notifications:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to Disable',
        description: error.message,
      });
      setPushNotificationStatus(prev => ({ ...prev, isLoading: false }));
    }
  };

  // Check push notification status when notifications tab is selected
  React.useEffect(() => {
    if (activeTab === 'notifications') {
      checkPushNotificationStatus();
    }
  }, [activeTab]);

  return (
    <div className={`${ysr2Mobile.shell} min-h-[100dvh] space-y-6 px-4 pt-4 pb-28`}>
      <div className={ysr2Mobile.orbBlue} />
      <div className={ysr2Mobile.orbPurple} />
      {/* Profile Picture Section */}
      <MobileCard className="p-6 text-center">
        <div className="relative inline-block">
          <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-purple-500/60 mx-auto mb-4">
            {formData.profile_picture_url ? (
              <img
                src={formData.profile_picture_url}
                alt="Profile"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gray-800/50 flex items-center justify-center">
                <User className="w-12 h-12 text-gray-400" />
              </div>
            )}
          </div>
          
          <motion.button
            onClick={() => setShowImagePicker(true)}
            className="absolute bottom-0 right-0 w-8 h-8 bg-purple-600 border-2 border-purple-500 rounded-full flex items-center justify-center"
            whileTap={{ scale: 0.9 }}
          >
            <Camera className="w-4 h-4 text-white" />
          </motion.button>
        </div>

        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-white">
            {formData.artist_name || 'Artist Name'}
          </h3>
          <p className="text-sm text-gray-400">
            {formData.bio || 'Add your bio to tell your audience about yourself'}
          </p>
        </div>

        {formData.profile_picture_url && (
          <motion.button
            onClick={onRemoveImage}
            className="mt-3 text-red-400 text-sm hover:text-red-300"
            whileTap={{ scale: 0.95 }}
          >
            Remove Photo
          </motion.button>
        )}
      </MobileCard>

      {/* Tab Navigation */}
      <div className="grid grid-cols-3 gap-2">
        {tabs.map((tab, index) => (
          <motion.button
            key={tab.id}
            className={`flex items-center justify-center gap-1.5 p-2.5 rounded-xl border-2 transition-all duration-200 ${
              activeTab === tab.id
                ? 'bg-purple-900/20 border-purple-500/60 text-purple-400'
                : 'bg-gray-800/30 border-gray-600/40 text-gray-400'
            }`}
            onClick={() => setActiveTab(tab.id)}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <tab.icon className="w-4 h-4 shrink-0" />
            <span className="text-xs font-medium sm:text-sm">{tab.label}</span>
          </motion.button>
        ))}
      </div>

      {/* Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'profile' ? (
          <motion.div
            key="profile"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            {/* Artist Name */}
            <MobileCard className="p-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Artist Name *
              </label>
              <input
                type="text"
                name="artist_name"
                value={formData.artist_name}
                onChange={onInputChange}
                placeholder="Enter your artist name"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
                required
              />
            </MobileCard>

            {/* Full Name */}
            <MobileCard className="p-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Full Name {profile?.stripe_account_id && <span className="text-xs text-gray-400">(Locked)</span>}
              </label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name || ''}
                onChange={onInputChange}
                placeholder="Enter your full name"
                disabled={!!profile?.stripe_account_id}
                className={`w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 ${profile?.stripe_account_id ? 'opacity-50 cursor-not-allowed' : ''}`}
              />
              {profile?.stripe_account_id && (
                <p className="text-xs text-amber-200 mt-1">
                  Full Name cannot be changed once Stripe is connected
                </p>
              )}
            </MobileCard>


            {/* Bio */}
            <MobileCard className="p-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Bio
              </label>
              <textarea
                name="bio"
                value={formData.bio}
                onChange={onInputChange}
                placeholder="Tell your audience about yourself..."
                rows={4}
                className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-purple-500 resize-none"
              />
            </MobileCard>

            {/* URL Slug */}
            <MobileCard className="p-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Profile URL
              </label>
              <div className="text-gray-400 text-sm mb-2">yoursongrequest.com/</div>
              <input
                type="text"
                name="url_slug"
                value={formData.url_slug}
                onChange={(e) => {
                  // Prevent changes if Profile URL already exists
                  if (profile?.url_slug) {
                    return;
                  }
                  
                  const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                  onInputChange({ target: { name: 'url_slug', value } });
                }}
                placeholder="your-artist-name"
                disabled={!!profile?.url_slug}
                className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-gray-400 placeholder-gray-500 focus:outline-none focus:border-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
              />
              {profile?.url_slug && (
                <p className="text-xs text-gray-500 mt-1">Profile URL cannot be changed once set</p>
              )}
            </MobileCard>

            {/* Social Links */}
            <MobileCard className="p-4">
              <h3 className="text-sm font-medium text-gray-300 mb-3">Social Media</h3>
              <div className="space-y-3">
                <input
                  type="text"
                  name="social_facebook"
                  value={formData.social_facebook}
                  onChange={onInputChange}
                  placeholder="Facebook URL"
                  className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
                />
                <input
                  type="text"
                  name="social_x"
                  value={formData.social_x}
                  onChange={onInputChange}
                  placeholder="X (Twitter) URL"
                  className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
                />
                <input
                  type="text"
                  name="social_instagram"
                  value={formData.social_instagram}
                  onChange={onInputChange}
                  placeholder="Instagram URL"
                  className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
                />
                <input
                  type="text"
                  name="social_tiktok"
                  value={formData.social_tiktok}
                  onChange={onInputChange}
                  placeholder="TikTok URL"
                  className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
                />
              </div>
            </MobileCard>

            {typeof onCountryChange === 'function' && (
              <MobileCard className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Globe className="w-5 h-5 text-purple-400" />
                  <h3 className="text-sm font-semibold text-white">Country & region</h3>
                </div>
                <p className="text-xs text-gray-400 mb-3">
                  Sets your currency and pricing for requests. Change this if you picked the wrong country at signup.
                </p>
                <label className="block text-sm font-medium text-gray-300 mb-2">Country of residence</label>
                <select
                  value={country || 'GB'}
                  onChange={(e) => onCountryChange(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:border-purple-500"
                >
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.label}
                    </option>
                  ))}
                </select>
                <p className="text-sm text-gray-400 mt-2">
                  Currency: <span className="text-white font-medium">{currency || 'GBP'}</span>
                </p>
                {profile?.stripe_account_id && (
                  <div className="mt-3 p-3 rounded-lg bg-yellow-900/20 border border-yellow-700/50">
                    <p className="text-xs text-yellow-200 flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                      <span>
                        Your payout account was created for a specific country. Changing this updates YSR display and catalogue pricing; your payout account country does not change.
                      </span>
                    </p>
                  </div>
                )}
              </MobileCard>
            )}

            {/* Save Button */}
            <motion.button
              onClick={onSave}
              disabled={loading}
              className={`w-full ${getMobileStyles.button('primary', 'lg')}`}
              whileTap={{ scale: 0.95 }}
            >
              <div className="flex items-center justify-center space-x-2">
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-5 h-5" />
                )}
                <span>{loading ? 'Saving...' : 'Save Profile'}</span>
              </div>
            </motion.button>
          </motion.div>
        ) : activeTab === 'security' ? (
          <motion.div
            key="security"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            <MobileCard className="p-6 text-center">
              <Shield className="w-12 h-12 text-blue-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Security Settings</h3>
              <p className="text-gray-400 text-sm">
                Manage your account security and privacy settings
              </p>
            </MobileCard>

            <MobileCard className="p-4">
              <h3 className="text-sm font-medium text-gray-300 mb-3">Account Security</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                  <div>
                    <div className="text-white font-medium">Email</div>
                    <div className="text-gray-400 text-sm">{profile?.email || 'Not set'}</div>
                  </div>
                  <motion.button
                    onClick={() => setShowEmailModal(true)}
                    className="text-blue-400 text-sm hover:text-blue-300"
                    whileTap={{ scale: 0.95 }}
                  >
                    Change
                  </motion.button>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                  <div>
                    <div className="text-white font-medium">Password</div>
                    <div className="text-gray-400 text-sm">••••••••</div>
                  </div>
                  <motion.button
                    onClick={() => setShowPasswordModal(true)}
                    className="text-blue-400 text-sm hover:text-blue-300"
                    whileTap={{ scale: 0.95 }}
                  >
                    Change
                  </motion.button>
                </div>
              </div>
            </MobileCard>

            {/* Delete Account Section */}
            <MobileCard className="p-4">
              <h3 className="text-sm font-medium text-red-400 mb-3">Danger Zone</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-red-900/20 border border-red-500/30 rounded-lg">
                  <div>
                    <div className="text-red-300 font-medium">Delete Account</div>
                    <div className="text-red-400 text-sm">This action cannot be undone</div>
                  </div>
                  <motion.button
                    onClick={() => setShowDeleteModal(true)}
                    className="text-red-400 text-sm hover:text-red-300 border border-red-500/50 px-3 py-1 rounded-lg"
                    whileTap={{ scale: 0.95 }}
                  >
                    Delete
                  </motion.button>
                </div>
              </div>
            </MobileCard>
          </motion.div>
        ) : activeTab === 'gigs' ? (
          <motion.div
            key="gigs"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            <ProfileGigsSection variant="mobile" />
          </motion.div>
        ) : activeTab === 'support' ? (
          <motion.div
            key="support"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            <MobileCard className="p-4">
              <h3 className="text-lg font-semibold text-white mb-1">Support</h3>
              <p className="text-sm text-gray-400 mb-4">Messages from the YSR team and your replies.</p>
              <SupportThreadView />
            </MobileCard>
          </motion.div>
        ) : (
          <motion.div
            key="notifications"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            {/* Push Notifications Header */}
            <MobileCard className="p-6 text-center">
              <Bell className="w-12 h-12 text-blue-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Push Notifications</h3>
              <p className="text-gray-400 text-sm">
                Enable notifications to receive alerts for song requests and tips
              </p>
            </MobileCard>

            {/* Status Display */}
            <MobileCard className="p-4">
              <div className="flex items-center justify-between p-3 bg-gray-800/30 rounded-lg">
                <div className="flex items-center gap-3">
                  {(() => {
                    const hint = pushNotificationStatus.setupHint;
                    const sup = pushNotificationStatus.isSupported;
                    const active = sup && pushNotificationStatus.permission === 'granted' && pushNotificationStatus.isSubscribed;
                    if (active) {
                      return <CheckCircle className="h-5 w-5 text-green-500" />;
                    }
                    if (hint === 'ios_install' || hint === 'ios_push_unavailable') {
                      return <AlertTriangle className="h-5 w-5 text-amber-500" />;
                    }
                    if (sup && pushNotificationStatus.permission === 'denied') {
                      return <XCircle className="h-5 w-5 text-red-500" />;
                    }
                    if (sup) {
                      return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
                    }
                    return <XCircle className="h-5 w-5 text-red-500" />;
                  })()}
                  <div>
                    <p className="font-medium text-white">Notification Status</p>
                    <p className="text-sm text-gray-400">
                      {pushNotificationStatus.setupHint === 'ios_install'
                        ? 'Open YSR from your Home Screen icon, then enable push here.'
                        : pushNotificationStatus.setupHint === 'ios_push_unavailable'
                        ? 'This home screen app cannot use Web Push yet. Use iOS/iPadOS 16.4+ and add via Share → Add to Home Screen (with a manifest), not only a bookmark.'
                        : pushNotificationStatus.setupHint === 'insecure'
                        ? 'Push needs a secure (HTTPS) connection.'
                        : pushNotificationStatus.setupHint === 'no_sw'
                        ? 'This browser does not support background notifications.'
                        : !pushNotificationStatus.isSupported
                        ? 'Not supported in this browser.'
                        : pushNotificationStatus.permission === 'granted' && pushNotificationStatus.isSubscribed
                        ? 'Active'
                        : pushNotificationStatus.permission === 'denied'
                        ? 'Permission denied — enable notifications in browser or system settings.'
                        : pushNotificationStatus.permission === 'default'
                        ? 'Not enabled — tap Enable below.'
                        : 'Unknown'}
                    </p>
                  </div>
                </div>
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                  pushNotificationStatus.setupHint === 'ios_install' || pushNotificationStatus.setupHint === 'ios_push_unavailable' ? 'bg-amber-100 text-amber-900' :
                  !pushNotificationStatus.isSupported ? 'bg-red-100 text-red-800' :
                  pushNotificationStatus.permission === 'granted' && pushNotificationStatus.isSubscribed ? 'bg-green-100 text-green-800' :
                  pushNotificationStatus.permission === 'denied' ? 'bg-red-100 text-red-800' :
                  'bg-yellow-100 text-yellow-800'
                }`}>
                  {pushNotificationStatus.setupHint === 'ios_install' ? 'Install app' :
                   pushNotificationStatus.setupHint === 'ios_push_unavailable' ? 'Update / reinstall' :
                   !pushNotificationStatus.isSupported ? 'Not supported' :
                   pushNotificationStatus.permission === 'granted' && pushNotificationStatus.isSubscribed ? 'Active' :
                   pushNotificationStatus.permission === 'denied' ? 'Denied' :
                   'Not enabled'}
                </div>
              </div>
            </MobileCard>

            {/* Action Buttons */}
            <MobileCard className="p-4">
              <div className="space-y-3">
                {!pushNotificationStatus.isSubscribed && pushNotificationStatus.isSupported && (
                  <motion.button
                    onClick={handleEnablePushNotifications}
                    disabled={pushNotificationStatus.isLoading}
                    className={`w-full ${getMobileStyles.button('primary', 'md')}`}
                    whileTap={{ scale: 0.95 }}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      {pushNotificationStatus.isLoading ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Bell className="w-4 h-4" />
                      )}
                      <span>{pushNotificationStatus.isLoading ? 'Enabling...' : 'Enable Push Notifications'}</span>
                    </div>
                  </motion.button>
                )}
                
                {pushNotificationStatus.isSubscribed && (
                  <>
                    <motion.button
                      onClick={handleTestNotification}
                      disabled={pushNotificationStatus.isLoading}
                      className={`w-full ${getMobileStyles.button('outline', 'md')}`}
                      whileTap={{ scale: 0.95 }}
                    >
                      <div className="flex items-center justify-center space-x-2">
                        {pushNotificationStatus.isLoading ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Bell className="w-4 h-4" />
                        )}
                        <span>{pushNotificationStatus.isLoading ? 'Sending...' : 'Send Test Notification'}</span>
                      </div>
                    </motion.button>
                    <motion.button
                      onClick={handleDisablePushNotifications}
                      disabled={pushNotificationStatus.isLoading}
                      className={`w-full ${getMobileStyles.button('outline', 'md')}`}
                      whileTap={{ scale: 0.95 }}
                    >
                      <span>{pushNotificationStatus.isLoading ? 'Working...' : 'Disable Notifications'}</span>
                    </motion.button>
                  </>
                )}

                <motion.button
                  onClick={checkPushNotificationStatus}
                  disabled={pushNotificationStatus.isLoading}
                  className={`w-full ${getMobileStyles.button('outline', 'md')}`}
                  whileTap={{ scale: 0.95 }}
                >
                  <span>Refresh Status</span>
                </motion.button>
              </div>
            </MobileCard>

            {/* Instructions */}
            <MobileCard className="p-4">
              <h4 className="font-medium text-blue-300 mb-2">Install the app (recommended)</h4>
              <p className="text-sm text-blue-100/85 mb-3">
                Web push works best when you add YSR to your home screen, then enable notifications below.
              </p>
              <ul className="text-sm text-blue-200/95 space-y-3 list-none">
                <li>
                  <span className="font-semibold text-white">iPhone or iPad (Safari)</span>
                  <ol className="list-decimal list-inside mt-1 space-y-1 text-blue-100/90">
                    <li>Tap the Share button</li>
                    <li>Tap <strong className="text-white">Add to Home Screen</strong></li>
                    <li>Open YSR using the new home screen icon</li>
                    <li>Return here → Alerts → <strong className="text-white">Enable Push Notifications</strong> and tap <strong className="text-white">Allow</strong></li>
                  </ol>
                </li>
                <li>
                  <span className="font-semibold text-white">Android (Chrome)</span>
                  <ol className="list-decimal list-inside mt-1 space-y-1 text-blue-100/90">
                    <li>Tap the browser menu (⋮)</li>
                    <li>Tap <strong className="text-white">Install app</strong> or <strong className="text-white">Add to Home screen</strong></li>
                    <li>Open YSR from the home screen icon</li>
                    <li>Return here → Alerts → enable notifications and accept the prompt</li>
                  </ol>
                </li>
                <li>
                  <span className="font-semibold text-white">Desktop (Chrome, Edge, Firefox)</span>
                  <p className="mt-1 text-blue-100/90">Use the install icon in the address bar if shown, or keep the tab pinned. Then enable notifications when prompted.</p>
                </li>
              </ul>
            </MobileCard>

            <MobileCard className="p-4">
              <h4 className="font-medium text-blue-300 mb-3">What you will get</h4>
              <ul className="text-sm text-blue-200 space-y-2 list-disc list-inside">
                <li>Alerts when a fan pays for a song request while you are not on the page</li>
                <li>Tip notifications when applicable</li>
                <li>Messages from YSR (payments, admin updates) when sent to your account</li>
                <li>Tap a notification to open your live dashboard</li>
              </ul>
            </MobileCard>

            {pushNotificationStatus.setupHint === 'ios_install' && (
              <MobileCard className="p-4 border border-amber-600/40">
                <h4 className="font-medium text-amber-200 mb-2">iPhone / iPad</h4>
                <p className="text-sm text-amber-100/90">
                  Web Push in Safari only works after you add YSR to the Home Screen and open the app from that icon — not from a regular tab. Follow the steps under <strong className="text-white">Install the app</strong> above, then tap <strong className="text-white">Refresh Status</strong>.
                </p>
              </MobileCard>
            )}

            {pushNotificationStatus.setupHint === 'ios_push_unavailable' && (
              <MobileCard className="p-4 border border-amber-600/40">
                <h4 className="font-medium text-amber-200 mb-2">Still no push from the Home Screen icon?</h4>
                <p className="text-sm text-amber-100/90 space-y-2">
                  <span className="block">You need <strong className="text-white">iOS or iPadOS 16.4 or later</strong>.</span>
                  <span className="block">Remove the old icon, open YSR in Safari, then use <strong className="text-white">Share → Add to Home Screen</strong> so iOS saves it as a <strong className="text-white">web app</strong> (manifest). A plain bookmark can open in Safari instead and will not get push.</span>
                  <span className="block">Then open YSR from the <strong className="text-white">new</strong> icon and tap <strong className="text-white">Refresh Status</strong> here.</span>
                </p>
              </MobileCard>
            )}

            {!pushNotificationStatus.isSupported && pushNotificationStatus.setupHint !== 'ios_install' && pushNotificationStatus.setupHint !== 'ios_push_unavailable' && (
              <MobileCard className="p-4">
                <h4 className="font-medium text-red-300 mb-2">Push Notifications Not Supported</h4>
                <p className="text-sm text-red-200">
                  This browser or context does not support web push. Use a recent Chrome, Edge, Firefox, or Safari (from the Home Screen on iOS), over HTTPS.
                </p>
              </MobileCard>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hidden File Input */}
      <input
        type="file"
        accept="image/*"
        onChange={handleImageSelect}
        className="hidden"
        id="profile-image-upload"
      />

      {/* Email Update Modal */}
      <AnimatePresence>
        {showEmailModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-[99999]"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3 }}
              className="w-full max-w-md"
            >
              <MobileCard className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-2">
                    <Shield className="w-5 h-5 text-blue-400" />
                    <h2 className="text-xl font-semibold text-white">Update Email</h2>
                  </div>
                  <motion.button
                    onClick={() => setShowEmailModal(false)}
                    className="w-8 h-8 bg-gray-800/50 border border-gray-600 rounded-full flex items-center justify-center"
                    whileTap={{ scale: 0.9 }}
                  >
                    <span className="text-gray-400 text-lg">×</span>
                  </motion.button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      New Email
                    </label>
                    <input
                      type="email"
                      name="newEmail"
                      value={securityData.newEmail}
                      onChange={handleSecurityInputChange}
                      placeholder="Enter new email"
                      className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Current Password
                    </label>
                    <input
                      type="password"
                      name="currentPassword"
                      value={securityData.currentPassword}
                      onChange={handleSecurityInputChange}
                      placeholder="Enter current password"
                      className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                    />
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <motion.button
                      onClick={() => setShowEmailModal(false)}
                      className={`flex-1 ${getMobileStyles.button('outline', 'md')}`}
                      whileTap={{ scale: 0.95 }}
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      onClick={handleUpdateEmail}
                      disabled={updatingEmail || !securityData.newEmail || !securityData.currentPassword}
                      className={`flex-1 ${getMobileStyles.button('primary', 'md')}`}
                      whileTap={{ scale: 0.95 }}
                    >
                      <div className="flex items-center justify-center space-x-2">
                        {updatingEmail ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Save className="w-4 h-4" />
                        )}
                        <span>{updatingEmail ? 'Updating...' : 'Update Email'}</span>
                      </div>
                    </motion.button>
                  </div>
                </div>
              </MobileCard>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Password Update Modal */}
      <AnimatePresence>
        {showPasswordModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-[99999]"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3 }}
              className="w-full max-w-md"
            >
              <MobileCard className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-2">
                    <Shield className="w-5 h-5 text-blue-400" />
                    <h2 className="text-xl font-semibold text-white">Update Password</h2>
                  </div>
                  <motion.button
                    onClick={() => setShowPasswordModal(false)}
                    className="w-8 h-8 bg-gray-800/50 border border-gray-600 rounded-full flex items-center justify-center"
                    whileTap={{ scale: 0.9 }}
                  >
                    <span className="text-gray-400 text-lg">×</span>
                  </motion.button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Current Password
                    </label>
                    <input
                      type="password"
                      name="currentPassword"
                      value={securityData.currentPassword}
                      onChange={handleSecurityInputChange}
                      placeholder="Enter current password"
                      className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      New Password
                    </label>
                    <input
                      type="password"
                      name="newPassword"
                      value={securityData.newPassword}
                      onChange={handleSecurityInputChange}
                      placeholder="Enter new password"
                      className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={securityData.confirmPassword}
                      onChange={handleSecurityInputChange}
                      placeholder="Confirm new password"
                      className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                    />
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <motion.button
                      onClick={() => setShowPasswordModal(false)}
                      className={`flex-1 ${getMobileStyles.button('outline', 'md')}`}
                      whileTap={{ scale: 0.95 }}
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      onClick={handleUpdatePassword}
                      disabled={updatingPassword || !securityData.currentPassword || !securityData.newPassword || !securityData.confirmPassword || securityData.newPassword !== securityData.confirmPassword}
                      className={`flex-1 ${getMobileStyles.button('primary', 'md')}`}
                      whileTap={{ scale: 0.95 }}
                    >
                      <div className="flex items-center justify-center space-x-2">
                        {updatingPassword ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Save className="w-4 h-4" />
                        )}
                        <span>{updatingPassword ? 'Updating...' : 'Update Password'}</span>
                      </div>
                    </motion.button>
                  </div>
                </div>
              </MobileCard>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Account Modal */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-[99999]"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3 }}
              className="w-full max-w-md"
            >
              <MobileCard className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-2">
                    <Trash2 className="w-5 h-5 text-red-400" />
                    <h2 className="text-xl font-semibold text-white">Delete Account</h2>
                  </div>
                  <motion.button
                    onClick={() => setShowDeleteModal(false)}
                    className="w-8 h-8 bg-gray-800/50 border border-gray-600 rounded-full flex items-center justify-center"
                    whileTap={{ scale: 0.9 }}
                  >
                    <span className="text-gray-400 text-lg">×</span>
                  </motion.button>
                </div>

                <div className="space-y-4">
                  <div className="text-center">
                    <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-white mb-2">Warning</h3>
                    <p className="text-gray-400 text-sm mb-4">
                      This action cannot be undone. All your data, songs, and account information will be permanently deleted.
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Type 'delete' to confirm
                    </label>
                    <input
                      type="text"
                      name="deleteConfirmation"
                      value={securityData.deleteConfirmation}
                      onChange={handleSecurityInputChange}
                      placeholder="Type 'delete'"
                      className="w-full px-4 py-3 bg-gray-800/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-red-500"
                    />
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <motion.button
                      onClick={() => setShowDeleteModal(false)}
                      className={`flex-1 ${getMobileStyles.button('outline', 'md')}`}
                      whileTap={{ scale: 0.95 }}
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      onClick={handleDeleteAccount}
                      disabled={deletingAccount || securityData.deleteConfirmation !== 'delete'}
                      className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-red-800 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-xl transition-colors"
                      whileTap={{ scale: 0.95 }}
                    >
                      <div className="flex items-center justify-center space-x-2">
                        {deletingAccount ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                        <span>{deletingAccount ? 'Deleting...' : 'Delete Account'}</span>
                      </div>
                    </motion.button>
                  </div>
                </div>
              </MobileCard>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Image Picker Modal */}
      <AnimatePresence>
        {showImagePicker && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-[99999]"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3 }}
              className="w-full max-w-md"
            >
              <MobileCard className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-2">
                    <Camera className="w-5 h-5 text-purple-400" />
                    <h2 className="text-xl font-semibold text-white">Profile Picture</h2>
                  </div>
                  <motion.button
                    onClick={() => setShowImagePicker(false)}
                    className="w-8 h-8 bg-gray-800/50 border border-gray-600 rounded-full flex items-center justify-center"
                    whileTap={{ scale: 0.9 }}
                  >
                    <span className="text-gray-400 text-lg">×</span>
                  </motion.button>
                </div>

                <div className="space-y-4">
                  <motion.button
                    onClick={() => document.getElementById('profile-image-upload').click()}
                    className={`w-full ${getMobileStyles.button('primary', 'md')}`}
                    disabled={uploadingImage}
                    whileTap={{ scale: 0.95 }}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      {uploadingImage ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                      <span>{uploadingImage ? 'Uploading...' : 'Choose Photo'}</span>
                    </div>
                  </motion.button>

                  <motion.button
                    onClick={() => setShowImagePicker(false)}
                    className={`w-full ${getMobileStyles.button('outline', 'md')}`}
                    whileTap={{ scale: 0.95 }}
                  >
                    Cancel
                  </motion.button>
                </div>
              </MobileCard>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MobileProfile;
